import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { SvgIcon } from 'common/react/components/SvgIcon';
import { TeamIcon } from 'common/react/components/TeamIcon';
import { getTranslation } from 'common/react/utils/translations';
import { Loader } from 'common/react/components/Loader';
import { EmptyState } from 'common/react/components/EmptyState';
import { RoundVotes } from '../../../player-awards-tracker/js/components/round-votes';
import { TeamWatermarkBackground } from '../../../statspro/js/components/TeamWatermarkBackground';
import { PlayerPhoto } from 'common/react/components/PlayerPhoto';
import { useLeaderboardDataWithPredictedVotes } from './hooks/use-leaderboard-with-predictor-data';

export const LeadderboardEmbeddable = ({
    title,
    subtitle,
    linkTo,
    linkToLabel,
    seasonPid,
    seasonId,
    competitionId,
    numberOfPlayers,
    clubPid
}) => {
    const isWebView =
        PULSE.core.url.getParam('webview') === 'true'
            ? true
            : globalThis.IS_ARTICLE_API === true;

    const clubFilter =
        clubPid && clubPid !== 'all'
            ? PULSE.app.common.CONSTANTS.TEAM_ABBR[clubPid]?.toLowerCase()
            : 'all';

    const { data, rounds, isLoading } = useLeaderboardDataWithPredictedVotes({
        competitionId: competitionId,
        compSeasonId: seasonId,
        compSeasonPid: seasonPid,
        pageSize: numberOfPlayers,
        clubFilter: clubFilter
    });

    const [noDataAvailable, setNoDataAvailable] = useState(false);

    useEffect(() => {
        // Set no data available to show the empty state
        if (!isLoading && data.leaderboard?.length === 0) {
            setNoDataAvailable(true);
        } else {
            setNoDataAvailable(false);
        }
    }, [data, data?.status, isLoading]);

    const isAFLW = PULSE.app.common.match.getCompAbbr(seasonPid) === 'AFLW';

    const isWce = (teamId) =>
        PULSE.app.common.team.getTeamAbbr(teamId) === 'wce';

    const filteredRounds = rounds
        ?.filter(
            (item) =>
                (item.name.includes('Round') || item.name.includes('Week')) &&
                !item.name.includes('Final')
        )
        .sort((roundA, roundB) =>
            roundA.roundNumber > roundB.roundNumber ? 1 : -1
        );

    const tableHeader = filteredRounds ? (
        <thead>
            <tr className="player-awards-leaderboard-embeddable__row">
                <th className="player-awards-leaderboard-embeddable__cell player-awards-leaderboard-embeddable__cell--name-header">
                    {getTranslation('label.player')}
                </th>
                <th className="player-awards-leaderboard-embeddable__cell player-awards-leaderboard-embeddable__cell--rounds">
                    <div className="player-awards-round-votes">
                        {filteredRounds.map((round) => {
                            return (
                                <div
                                    className="player-awards-round-votes__round-header"
                                    key={`player-award-embeddable-table-head-round-${round.id}`}
                                >
                                    {round?.abbreviation === 'OR'
                                        ? 'OR'
                                        : round?.roundNumber}
                                </div>
                            );
                        })}
                    </div>
                </th>
                <th className="player-awards-leaderboard-embeddable__cell player-awards-leaderboard-embeddable__cell--total">
                    {getTranslation('label.total')}
                </th>
            </tr>
        </thead>
    ) : null;

    return (
        <div
            className={`${
                isAFLW
                    ? 'player-awards-embeddable__aflw-theme'
                    : 'player-awards-embeddable__aflm-theme'
            } 
            ${isWebView ? 'player-awards-embeddable--static-dimensions' : ''}`}
        >
            <div className="player-awards-embeddable__header">
                {clubFilter && clubFilter !== 'all' && (
                    <>
                        <TeamWatermarkBackground
                            className="stats-card-featured-player__bg stats-card-featured-player__bg--no-border-radius"
                            side="right"
                            providerId={clubPid}
                        />
                        <TeamIcon
                            className={
                                'icon player-awards-embeddable__club-icon'
                            }
                            theme={PULSE.app.common.team.getTeamLogoTheme(
                                clubFilter,
                                isWce(clubPid) ? '' : 'b'
                            )}
                            providerId={clubPid}
                        />
                    </>
                )}
                <div
                    className={`player-awards-embeddable__logo ${
                        isAFLW ? 'player-awards-embeddable__logo--aflw' : ''
                    }`}
                ></div>
                <div className="player-awards-embeddable__header-text-container">
                    <h3 className="player-awards-embeddable__title">
                        {title ?? ''}
                    </h3>
                    <p className="player-awards-embeddable__subtitle">
                        {subtitle ?? ''}
                    </p>
                </div>
            </div>

            {isLoading && (
                <div className="player-awards-leaderboard-embeddable__loader">
                    <Loader />
                </div>
            )}

            {noDataAvailable && (
                <EmptyState
                    titleTranslation="label.stats.error.noStatsAvailable"
                    summaryTranslation=""
                />
            )}

            {data?.leaderboard?.length > 0 &&
                !noDataAvailable &&
                !isLoading && (
                    <table className="player-awards-leaderboard-embeddable__table">
                        {tableHeader}
                        <tbody>
                            {data?.leaderboard
                                ?.slice(0, numberOfPlayers)
                                .map((item) => (
                                    <tr
                                        key={`player-awards-embeddable-${item.player.playerId}`}
                                        className={`player-awards-leaderboard-embeddable__row ${
                                            item.player.eligible === false
                                                ? 'player-awards-leaderboard-embeddable__row--ineligible'
                                                : ''
                                        }`}
                                    >
                                        <td className="player-awards-leaderboard-embeddable__cell player-awards-leaderboard-embeddable__cell--name">
                                            <div className="player-awards-leaderboard-embeddable__name-block">
                                                <div className="player-awards-leaderboard-embeddable__left_side">
                                                    <div className="player-awards-leaderboard-embeddable__left-side-wrapper">
                                                        <PlayerPhoto
                                                            className={
                                                                'player-awards-leaderboard-embeddable__photo'
                                                            }
                                                            photoUrl={
                                                                item.player
                                                                    .photoURL
                                                            }
                                                            scale={'0.1'}
                                                            alt={`${getTranslation(
                                                                'label.stats.headshot.alt'
                                                            )} ${
                                                                item.player
                                                                    .givenName
                                                            } ${
                                                                item.player
                                                                    .surname
                                                            }}`}
                                                        />
                                                        <div className="player-awards-leaderboard-embeddable__name-wrapper">
                                                            <span className="player-awards-leaderboard-embeddable__name">
                                                                {
                                                                    item.player
                                                                        .givenName
                                                                }{' '}
                                                                {
                                                                    item.player
                                                                        .surname
                                                                }
                                                            </span>
                                                            {item.player
                                                                .eligible ===
                                                            false ? (
                                                                <span className="player-awards-leaderboard-embeddable__ineligible">
                                                                    {getTranslation(
                                                                        'label.player-awards.predictor.ineligible'
                                                                    )}
                                                                </span>
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="player-awards-leaderboard-embeddable__right_side">
                                                    {item.player.winner ? (
                                                        <div
                                                            className={`player-awards-leaderboard-embeddable__winner-badge ${
                                                                isAFLW
                                                                    ? 'player-awards-leaderboard-embeddable__winner-badge--aflw'
                                                                    : ''
                                                            }`}
                                                        ></div>
                                                    ) : null}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="player-awards-leaderboard-embeddable__cell player-awards-leaderboard-embeddable__cell--rounds">
                                            <RoundVotes
                                                data={item}
                                                rounds={filteredRounds}
                                                seasonPid={seasonPid}
                                            />
                                        </td>
                                        <td className="player-awards-leaderboard-embeddable__cell player-awards-leaderboard-embeddable__cell--total-votes">
                                            {item.totalVotes ?? '0'}
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                )}

            {linkTo && linkToLabel && (
                <div className="player-awards-embeddable__footer">
                    <a
                        href={linkTo}
                        className="player-awards-embeddable__footer-link"
                    >
                        {linkToLabel}
                        <SvgIcon icon="arrow-right" className="icon" />
                    </a>
                </div>
            )}
        </div>
    );
};

LeadderboardEmbeddable.propTypes = {
    title: PropTypes.string,
    subtitle: PropTypes.string,
    linkTo: PropTypes.string,
    linkToLabel: PropTypes.string,
    seasonPid: PropTypes.string.isRequired,
    seasonId: PropTypes.string.isRequired,
    numberOfPlayers: PropTypes.string.isRequired,
    clubPid: PropTypes.string,
    competitionId: PropTypes.string
};
